import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MessageSquare, RefreshCw, Target, TrendingUp, Users } from 'lucide-react';
import { Card } from '../components/ui/card';

export default function PolicyAdvocacy() {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5F8D] text-white pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Policy & Advocacy
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed">
              Evidence-based engagement for structural improvement
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-5xl mx-auto">
            {/* Introduction */}
            <div className="mb-16">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                CSED approaches policy and advocacy as a process of <strong>constructive, evidence-based engagement, rather than as a mechanism for representation or lobbying</strong>.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                In complex development environments, the effectiveness of policy is determined not only by its design, but by how well it aligns with institutional capacity, capital flows, and ground-level realities. CSED's role is to strengthen this alignment by contributing insights that are both analytically grounded and operationally relevant.
              </p>
            </div>

            {/* Stakeholder Engagement */}
            <Card className="p-8 mb-12 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200">
              <h2 className="text-2xl font-bold text-[#1E3A5F] mb-6 flex items-center gap-3">
                <Users className="h-8 w-8 text-[#E87722]" />
                Informed Dialogue & Coordinated Action
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Through its work, CSED engages with policymakers, institutional stakeholders, industry bodies, and the development sector to facilitate <strong>informed dialogue and coordinated action</strong>.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="font-bold text-[#1E3A5F] mb-3">Policy Stakeholders</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#E87722] rounded-full mt-2"></div>
                      <span>Government bodies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#E87722] rounded-full mt-2"></div>
                      <span>Regulatory authorities</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#E87722] rounded-full mt-2"></div>
                      <span>Planning commissions</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="font-bold text-[#1E3A5F] mb-3">Implementation Partners</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#E87722] rounded-full mt-2"></div>
                      <span>Industry associations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#E87722] rounded-full mt-2"></div>
                      <span>Development organizations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#E87722] rounded-full mt-2"></div>
                      <span>Institutional funders</span>
                    </li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* Focus Areas */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-[#1E3A5F] mb-8">Strategic Focus Areas</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The emphasis is on identifying systemic gaps, reducing inefficiencies, and improving the overall coherence of development efforts.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    title: 'Enterprise Stability',
                    description: 'Policy inputs on MSME governance, capital access, and operational resilience',
                    icon: TrendingUp
                  },
                  {
                    title: 'Capital Efficiency',
                    description: 'Frameworks for transparent and measurable CSR and philanthropic deployment',
                    icon: Target
                  },
                  {
                    title: 'Social Impact Systems',
                    description: 'M&E standards and accountability mechanisms for development programs',
                    icon: Users
                  },
                  {
                    title: 'Environmental Risk Integration',
                    description: 'Climate resilience and sustainability governance frameworks',
                    icon: RefreshCw
                  }
                ].map((area, idx) => {
                  const Icon = area.icon;
                  return (
                    <Card key={idx} className="p-6 hover:shadow-xl transition-all">
                      <Icon className="h-10 w-10 text-[#E87722] mb-4" />
                      <h3 className="text-xl font-bold text-[#1E3A5F] mb-3">{area.title}</h3>
                      <p className="text-gray-700">{area.description}</p>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Evidence-Based Approach */}
            <Card className="p-8 mb-12 border-l-4 border-[#E87722]">
              <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">Rooted in Research & Field Understanding</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                CSED's policy contributions are rooted in its <strong>research and field-level understanding</strong>. This allows it to provide structured inputs on areas such as:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <MessageSquare className="h-6 w-6 text-[#E87722] flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Enterprise stability and economic governance</span>
                </li>
                <li className="flex items-start gap-3">
                  <MessageSquare className="h-6 w-6 text-[#E87722] flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Capital efficiency and impact measurement</span>
                </li>
                <li className="flex items-start gap-3">
                  <MessageSquare className="h-6 w-6 text-[#E87722] flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Social impact systems and M&E frameworks</span>
                </li>
                <li className="flex items-start gap-3">
                  <MessageSquare className="h-6 w-6 text-[#E87722] flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Environmental risk integration and sustainability governance</span>
                </li>
              </ul>
            </Card>

            {/* Non-Partisan Approach */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6">Institutionally Disciplined Approach</h2>
              <Card className="p-8 bg-gradient-to-br from-orange-50 to-amber-50">
                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                  By maintaining a <strong>non-partisan and institutionally disciplined approach</strong>, CSED ensures that its engagement remains focused on:
                </p>
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#E87722] text-white rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">
                      ✓
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1E3A5F] mb-2">Long-term Structural Improvement</h3>
                      <p className="text-gray-700">Sustainable systems change over advocacy wins</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#E87722] text-white rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">
                      ✓
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1E3A5F] mb-2">Evidence Over Opinion</h3>
                      <p className="text-gray-700">Data-driven recommendations grounded in research</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Continuous Cycle */}
            <Card className="p-8 mb-12 bg-white border-2 border-blue-200">
              <h2 className="text-2xl font-bold text-[#1E3A5F] mb-6 flex items-center gap-3">
                <RefreshCw className="h-8 w-8 text-[#E87722]" />
                A Continuous Improvement Cycle
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Policy and advocacy, in this framework, are not isolated functions. They are part of a <strong>continuous cycle</strong>:
              </p>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { num: '1', label: 'Research informs dialogue' },
                  { num: '2', label: 'Dialogue informs action' },
                  { num: '3', label: 'Action generates feedback' },
                  { num: '4', label: 'Feedback drives refinement' }
                ].map((step, idx) => (
                  <div key={idx} className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#E87722] to-[#d66a1a] text-white rounded-full flex items-center justify-center mx-auto mb-3 text-2xl font-bold">
                      {step.num}
                    </div>
                    <p className="text-sm font-semibold text-gray-700">{step.label}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Closing Statement */}
            <div className="mt-16 text-center bg-gradient-to-r from-[#1E3A5F] to-[#2D5F8D] text-white p-12 rounded-xl">
              <h2 className="text-2xl font-bold mb-4">Creating Better-Aligned Systems</h2>
              <p className="text-lg text-blue-100 leading-relaxed max-w-3xl mx-auto mb-4">
                The objective is not influence for its own sake, but the creation of <strong>better-aligned systems that deliver measurable and sustainable outcomes</strong>.
              </p>
              <p className="text-sm text-blue-200 italic">
                Policy engagement as a pathway to structural improvement, not advocacy for representation.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
